import queryString from 'query-string'
import { FilterRecord, FilterValue } from './ReportFilter'

// Helper function to clean filters by removing empty string values and NaN
export const cleanFilters = (filters: FilterRecord) => {
    const cleaned: FilterRecord = {}
    Object.keys(filters).forEach((key) => {
        const value = filters[key]

        // Handle arrays (like service_id)
        if (Array.isArray(value)) {
            const filteredArray = value.filter((v) => v !== '' && v !== null && v !== undefined)
            if (filteredArray.length > 0) {
                cleaned[key] = filteredArray
            }
        }
        // Handle non-array values
        else if (value !== '' && value !== null && value !== undefined) {
            // Skip string 'NaN' values
            if (value === 'NaN') {
                return
            }
            // Skip actual NaN values (but allow string values that can't be converted to numbers)
            if (typeof value === 'number' && isNaN(value)) {
                return
            }
            cleaned[key] = value
        }
    })
    return cleaned
}

// Helper function to parse service_id from URL
export const parseServiceId = (serviceId: unknown): string | number[] => {
    if (Array.isArray(serviceId)) {
        return serviceId.map((id) => parseInt(id, 10))
    }
    if (typeof serviceId === 'string' && serviceId.includes(',')) {
        const ids = serviceId
            .split(',')
            .filter((id) => id.trim() !== '')
            .map((id) => parseInt(id, 10))
        return ids.length > 1 ? ids : String(ids[0] || '')
    }
    return serviceId ? String(serviceId) : ''
}

// Helper function to update URL with new query parameters
export const updateUrlWithFilters = (
    location: { pathname: string },
    field: string,
    value: FilterValue,
    currentQuery: FilterRecord,
) => {
    // Handle different value types
    let urlValue = value
    if (value === '' || value === null || value === undefined) {
        urlValue = undefined
    } else if (Array.isArray(value)) {
        // Keep arrays as arrays, don't convert to comma-separated
        urlValue = value.length > 0 ? value : undefined
    } else if (typeof value === 'string' && value.trim() === '') {
        urlValue = undefined
    }

    const newQuery = {
        ...currentQuery,
    }

    // Set the field value (replace completely, don't merge)
    newQuery[field] = urlValue

    // Remove undefined values to clean up the query
    Object.keys(newQuery).forEach((key) => {
        if (newQuery[key] === undefined) {
            delete newQuery[key]
        }
    })

    const search = queryString.stringify(newQuery, {
        skipNull: true,
        arrayFormat: 'bracket', // Use bracket format for arrays like service_id[]=11
        encode: false, // Don't encode arrays, let them be properly formatted
    })

    window.history.replaceState({}, '', `${location.pathname}?${search}`)
}

// Helper function to clear URL parameters
export const clearUrlParameters = (location: { pathname: string }) => {
    window.history.replaceState({}, '', location.pathname)
}

// Helper function to get month names for dynamic column headers
export const getMonthNames = () => {
    const now = new Date()
    const thirdMonth = new Date(now.getFullYear(), now.getMonth() - 2, 1)
    const secondMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const previousMonth = new Date(now.getFullYear(), now.getMonth(), 1)

    return {
        third: thirdMonth.toLocaleString('default', { month: 'long' }),
        second: secondMonth.toLocaleString('default', { month: 'long' }),
        previous: previousMonth.toLocaleString('default', { month: 'long' }),
    }
}

// Helper function to format currency values
export const formatCurrency = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined) return '-'
    return `€${parseFloat(value.toString()).toFixed(2)}`
}

// Helper function to format prospect value with euro symbols
export const formatProspectValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined || value === 0) return '-'
    return '€'.repeat(value)
}
